import React, { useEffect, useRef, useState } from "react";
import * as styles from "../styles/consent.css";
import { useConsentLabels } from "../hooks/useConsentLabels";
import {
  ConsentState,
  FULL_CONSENT,
  NO_CONSENT,
  applyConsent,
  hasConfiguredTags,
  onOpenConsentSettings,
  readConsent,
  watchConsentChanges,
  writeConsent,
} from "../utils/consent";

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

type ConsentBannerProps = { language: string };

const ConsentBanner: React.FC<ConsentBannerProps> = ({ language }) => {
  const labels = useConsentLabels(language);
  const [visible, setVisible] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  const modal = useRef<HTMLElement>(null);
  const banner = useRef<HTMLDivElement>(null);
  // Counted rather than keyed on `visible`: a visitor who has not decided yet
  // still has the banner on screen, so using the footer control produces no
  // `visible` transition at all and focus would never move.
  const [reopenCount, setReopenCount] = useState(0);
  const reopenedFrom = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!hasConfiguredTags()) return;

    const stopWatching = watchConsentChanges((state) => {
      setVisible(state === null);
      // A decision made in another tab hides this banner, so the policy opened
      // over it has to go with it. Left open it keeps its key listener bound to
      // an unrendered dialog, and reopening the settings later shows the policy
      // instead of the choices.
      // The trigger is dropped with it: that tab closed the banner without a
      // decision here, so there is nothing to return focus to.
      if (state !== null) {
        setPrivacyOpen(false);
        reopenedFrom.current = null;
      }
    });

    const stored = readConsent();
    if (stored) {
      // Re-applied on every page load: the tags live in the page, not in
      // localStorage, so a stored grant has to reload them here.
      applyConsent(stored);
    } else {
      setVisible(true);
    }

    const stopListening = onOpenConsentSettings((trigger) => {
      reopenedFrom.current = trigger;
      setVisible(true);
      setReopenCount((count) => count + 1);
    });

    return () => {
      stopListening();
      stopWatching();
    };
  }, []);

  useEffect(() => {
    if (!privacyOpen) return;

    // The policy opens over the banner, so focus has to come back to the link
    // that opened it; otherwise it falls to the top of the document.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeButton.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPrivacyOpen(false);
        return;
      }
      if (event.key !== "Tab" || !modal.current) return;
      // aria-modal only tells assistive tech the rest is inert; Tab still walks
      // out of the dialog into the page behind it unless it is wrapped here.
      const focusable = Array.from(modal.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const edge = event.shiftKey ? focusable[0] : focusable[focusable.length - 1];
      if (document.activeElement !== edge && modal.current.contains(document.activeElement)) return;
      event.preventDefault();
      (event.shiftKey ? focusable[focusable.length - 1] : focusable[0]).focus();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus();
    };
  }, [privacyOpen]);

  // Appearing on a first visit is unprompted, so taking focus then would be
  // hostile; only an explicit reopen moves it, which is why this is keyed on the
  // reopen counter and not on `visible`. The banner itself is focused rather than
  // one of its buttons, so the message is announced before the options. There is
  // deliberately no focus trap: the banner is non-modal and the page stays usable
  // while it is up.
  useEffect(() => {
    if (reopenCount === 0) return;
    banner.current?.focus();
  }, [reopenCount]);

  const decide = (state: ConsentState) => {
    writeConsent(state);
    setVisible(false);
    // Focus is returned here rather than from an effect cleanup, so that only a
    // decision made in this tab moves it. Hiding the banner because another tab
    // decided must leave focus and scroll position alone - the visitor is not
    // looking at this window.
    reopenedFrom.current?.focus();
    reopenedFrom.current = null;
  };

  if (!visible || !labels) return null;

  // Not role="dialog": focus stays in the page while this is up, so announcing it
  // as an open dialog would misdescribe it. A labelled region is the markup for a
  // non-modal bar. The policy modal rendered over it is a real dialog.
  return (
    <div
      ref={banner}
      tabIndex={-1}
      className={styles.banner}
      role="region"
      aria-live="polite"
      aria-label={labels.settings}
    >
      <div className={styles.bannerInner}>
        <p className={styles.text}>
          {labels.message}{" "}
          <button type="button" className={styles.link} onClick={() => setPrivacyOpen(true)}>
            {labels.privacyLink}
          </button>
        </p>
        <div className={styles.actions}>
          <button type="button" className={styles.choiceButton} onClick={() => decide(NO_CONSENT)}>
            {labels.reject}
          </button>
          <button type="button" className={styles.choiceButton} onClick={() => decide(FULL_CONSENT)}>
            {labels.accept}
          </button>
        </div>
      </div>
      {privacyOpen && (
        <div className={styles.modalBackdrop} onMouseDown={(event) => {
          if (event.target === event.currentTarget) setPrivacyOpen(false);
        }}>
          <section ref={modal} className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="privacy-policy-title">
            <header className={styles.modalHeader}>
              <h2 id="privacy-policy-title" className={styles.modalTitle}>{labels.privacyPolicy.frontmatter.title}</h2>
              <button
                ref={closeButton}
                type="button"
                className={styles.closeButton}
                aria-label={labels.close}
                onClick={() => setPrivacyOpen(false)}
              >
                ×
              </button>
            </header>
            <div className={styles.modalContent} dangerouslySetInnerHTML={{ __html: labels.privacyPolicy.html }} />
          </section>
        </div>
      )}
    </div>
  );
};

export default ConsentBanner;
